import {
  Box,
  FlexBox,
  Icon,
  Skeleton,
  Table,
  Text,
} from '@dixa/lib-shared-habla';
import { SourceConversation } from 'lib-knowledge-types';
import { convertChannelCasing } from 'lib-shared-api-utils';
import { ConversationsChannels } from 'lib-shared-legacy-types';

import { useReadSearchInfiniteQuery } from 'apis/search';
import { ConversationChannelIcon } from 'components/conversation/conversationChannelIcon';
import { UserProfile } from 'components/userProfile';

import styles from './sourceConversations.module.scss';

type SourceConversationsProps = {
  conversations: readonly SourceConversation[];
};

/**
 * Renders the conversations a multi-conversation article draft was based on
 * (#1509). The knowledge suggestions lib only knows each conversation's id +
 * similarity; this app-side component resolves the display details (requester,
 * subject, channel) via a single batched Search API call and is injected into
 * the lib through the `renderSourceConversations` prop.
 */
export function SourceConversations({
  conversations,
}: SourceConversationsProps): JSX.Element | null {
  // Guarded here rather than inside the list so no search request is made for
  // an empty set (hooks can't be called conditionally).
  if (!conversations.length) {
    return null;
  }

  return <SourceConversationsList conversations={conversations} />;
}

function SourceConversationsList({
  conversations,
}: SourceConversationsProps): JSX.Element {
  const csids = conversations.map((c) => c.csid);

  // `useReadSearchInfiniteQuery` stabilizes its arg internally, so the object
  // can be passed inline.
  const { results, isFetching, isError } = useReadSearchInfiniteQuery(
    {
      scope: 'conversations',
      filters: {
        strategy: 'all',
        filters: [
          {
            field: 'numeric_id',
            operator: 'is_one',
            values: csids.map((csid) => ({ value: String(csid) })),
          },
        ],
      },
      // Enriches `requested_by` into a full user (name + avatar).
      enrichmentSpec: ['requested_by'],
      pageSize: Math.max(csids.length, 1),
    },
    { infinite: false },
  );

  if (isError) {
    return (
      <Text.Paragraph subdued variant="4">
        Unable to load conversations.
      </Text.Paragraph>
    );
  }

  if (isFetching && !results.length) {
    // One skeleton row per source conversation, mirroring the loaded layout.
    return (
      <Table className={styles.table} framed={false}>
        <Table.Tbody>
          {conversations.map(({ csid }) => (
            <Table.Tr key={csid}>
              <Table.Td padding="300 0">
                <Skeleton height="28px" width="84px" borderRadius="100" />
              </Table.Td>
              <Table.Td padding="300 400" width="100%" maxWidth="0px">
                <FlexBox alignItems="center" gap="250">
                  <Skeleton height="40px" width="40px" borderRadius="50%" />
                  <FlexBox
                    flexDirection="column"
                    gap="100"
                    flex="1"
                    minWidth="0"
                  >
                    <Skeleton height="14px" width="60%" />
                    <Skeleton height="14px" width="80%" />
                  </FlexBox>
                </FlexBox>
              </Table.Td>
              <Table.Td padding="300 0">
                <FlexBox justifyContent="flex-end">
                  <Skeleton height="14px" width="56px" />
                </FlexBox>
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
    );
  }

  return (
    <Table className={styles.table} framed={false}>
      <Table.Tbody>
        {conversations.map(({ csid, similarityPercentage }) => {
          const match = results.find((result) => result.id === csid);
          return (
            <Table.Tr key={csid}>
              <Table.Td padding="300 0">
                <FlexBox
                  alignItems="stretch"
                  display="inline-flex"
                  borderRadius="100"
                  background="green.200"
                  style={{ overflow: 'hidden' }}
                >
                  <FlexBox
                    alignItems="center"
                    justifyContent="center"
                    padding="100 200"
                    color="neutral.000"
                    background="green.500"
                  >
                    {match?.initial_channel ? (
                      <ConversationChannelIcon
                        channel={
                          convertChannelCasing(
                            match.initial_channel,
                            'truncate',
                          ) as ConversationsChannels
                        }
                      />
                    ) : (
                      <Icon name="chat" />
                    )}
                  </FlexBox>
                  <Box
                    className={styles.badgeId}
                    padding="100 250"
                    color="green.800"
                  >
                    {csid}
                  </Box>
                </FlexBox>
              </Table.Td>
              {/* `width: 100%` absorbs the leftover space and `max-width: 0`
                  stops the cell claiming its content's width, so the id and
                  match columns size to their content and the subject
                  truncates instead of widening the panel. */}
              <Table.Td padding="300 400" width="100%" maxWidth="0px">
                <UserProfile
                  className={styles.userProfile}
                  user={match?.requested_by}
                  // Reserve the subject line (non-breaking space) when a
                  // conversation has no subject, so the name stays top-aligned
                  // and matches rows that do have a subject.
                  description={match?.subject || '\u00A0'}
                  avatarSize="40"
                  truncateName
                  truncateDescription
                  nameClassName={styles.name}
                  descriptionClassName={styles.subject}
                />
              </Table.Td>
              {/* Without `nowrap` the column collapses to the min-content
                  width of "match" and the percentage wraps onto its own line. */}
              <Table.Td padding="300 0" align="right" wrap="nowrap">
                <Text.Paragraph subdued variant="4">
                  {Math.round(similarityPercentage)}% match
                </Text.Paragraph>
              </Table.Td>
            </Table.Tr>
          );
        })}
      </Table.Tbody>
    </Table>
  );
}
